/**
 * My Day's reads (Employee rebuild C1) — one hook per endpoint, each on the
 * SAME query key the page that owns the data uses, so a check-in on My Day
 * refreshes My bookings and Office days, and a change there refreshes here.
 *
 *   my bookings today   GET /space/me/bookings?view=upcoming   (C3's key)
 *   my numbers          GET /hybrid/compliance/me              (C4's key)
 *   my week plan        GET /hybrid/my-schedule?weeks=9        (C4's query, shared)
 *   my guideline        GET /hybrid/policies/mine              (C4's key)
 *   how busy            GET /hybrid/busyness                   (C5's key)
 *   my team today       GET /hybrid/whos-in/people?scope=team
 *
 * Each is enabled only for a holder of the permission its route checks, so
 * a role without one sends nothing it would be refused.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { hasPermFor } from '@/utils/permissions';
import { QK_MY_BOOKINGS, type MyBookingRow } from '@/components/MyBookings/types';
import {
  QK_MY_COMPLIANCE, QK_MY_POLICY_OD, myScheduleQuery, plannedDayType,
  type MyCompliance, type MyPolicyAnswer, type MyScheduleEnvelope,
} from '@/components/OfficeDays/officeDays';
import type { WhosInPerson } from '@/pages/WhosInPage';

export interface BusyDay { date: string; occupancy_pct: number | null; booked_desks: number; team_planned_office: number }
export interface Busyness {
  building_id: string | null; building_name: string | null; bookable_desks: number;
  team_size: number; days: BusyDay[]; definition: string;
}
export interface TeamToday { date: string; scope: string; my_desk_id: string | null; people: WhosInPerson[] }

function usePerm(p: string): boolean {
  return hasPermFor(useAuthStore((s) => s.user), p);
}

export function useMyBookingsUpcoming() {
  const can = usePerm('booking:view');
  return { can, q: useQuery({
    queryKey: QK_MY_BOOKINGS('upcoming'),
    queryFn: async () => (await apiGet<{ bookings: MyBookingRow[] }>('/space/me/bookings?view=upcoming')).bookings ?? [],
    enabled: can,
    staleTime: 30_000,
  }) };
}

export function useMyCompliance() {
  const can = usePerm('hybrid:view');
  return { can, q: useQuery({
    queryKey: QK_MY_COMPLIANCE,
    queryFn: () => apiGet<MyCompliance>('/hybrid/compliance/me'),
    enabled: can,
    staleTime: 30_000,
  }) };
}

export function useMyWeekPlan() {
  const can = usePerm('hybrid:schedule_submit');
  // Office days' own query (Employee fix, bug 4): the same key, so one copy.
  return { can, q: useQuery({ ...myScheduleQuery, enabled: can }) };
}

export function useMyPolicy() {
  const can = usePerm('hybrid:view');
  return { can, q: useQuery({
    queryKey: QK_MY_POLICY_OD,
    queryFn: () => apiGet<MyPolicyAnswer>('/hybrid/policies/mine'),
    enabled: can,
    staleTime: 60_000,
  }) };
}

export function useBusyness() {
  const can = usePerm('hybrid:view');
  return { can, q: useQuery({
    queryKey: ['hybrid', 'busyness'],
    queryFn: () => apiGet<Busyness>('/hybrid/busyness'),
    enabled: can,
    staleTime: 5 * 60_000,
  }) };
}

export function useTeamToday() {
  const can = usePerm('hybrid:view');
  return { can, q: useQuery({
    queryKey: ['hybrid', 'whos-in', 'people', 'team', 'today'],
    queryFn: () => apiGet<TeamToday>('/hybrid/whos-in/people?scope=team'),
    enabled: can,
    staleTime: 60_000,
  }) };
}

/** A weekday's planned day type for me, from this week's plan — "office",
 *  "remote", "leave", … — or null when that day is not planned. The mapping is
 *  Office days' own (plannedDayType), so the two screens cannot disagree. */
export function plannedToday(env: MyScheduleEnvelope | undefined, weekday: string): string | null {
  // data[0] is always the current week: the route lists from this week on.
  return plannedDayType(env?.data?.[0], weekday);
}
